import React, { useState } from 'react';

import moment from 'moment';
import { useDispatch } from 'react-redux';

import Checkbox from '@material-ui/core/Checkbox';

import { deleteApplicantAutomation } from '../../../applicant-automations/actions/delete-applicant-automation';
import { updateStartDateTriggeredCommunication } from '../../../start-date-triggered-communication/actions/update-start-date-triggered-communication';

import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { isApplicantAutomation } from '../../utils/is-applicant-automation';

export const DeleteAutomationDialog = ({
  orgId,
  automation,
  hasFollowUps,
  onClose,
  turnOffAutomation,
}: {
  orgId: number;
  automation: ApplicantAutomation | StartDateTriggeredCommunication;
  hasFollowUps?: boolean;
  onClose: () => void;
  turnOffAutomation: () => void;
}) => {
  const dispatch = useDispatch();
  const [isDeletingFollowUps, setIsDeletingFollowUps] = useState<boolean>(false);

  const recipient = isApplicantAutomation(automation) ? 'applicants' : 'employees';
  const isActive = isApplicantAutomation(automation) ? automation.isActive : automation.isOn;
  const turnOffMessage = isActive
    ? `If you turn off your automation, instead of deleting, you will stop it from being sent to your ${recipient} and save your message for the future.`
    : `Currently this automation does not send to your ${recipient}, but you will lose this message if you delete it.`;
  const orgDefaultMessage =
    isApplicantAutomation(automation) && automation.defaultForOrgId
      ? 'This automation is an organization default, deleting it will remove it from all jobs including those with custom messages. This action cannot be undone.'
      : 'Deleting your automation cannot be undone.';

  const handleToggleDeletingFollowUps = () => setIsDeletingFollowUps(!isDeletingFollowUps);

  const deleteAutomation = () => {
    if (!isApplicantAutomation(automation)) {
      dispatch(
        updateStartDateTriggeredCommunication(
          automation.startDateTriggeredCommunicationId,
          { deletedAt: moment().utc() },
          orgId,
        ),
      );
    } else {
      dispatch(
        deleteApplicantAutomation(
          automation.applicantTriggeredCommunicationId,
          isDeletingFollowUps,
        ),
      );
    }

    onClose();
  };

  const handleTurnOff = () => {
    // Here we want to call turnOffAutomation after onClose because we might open
    // another dialog in the turnOffAutomations to offer to turn off follow ups
    onClose();
    turnOffAutomation();
  };

  const showDeleteFollowUps =
    isApplicantAutomation(automation) && hasFollowUps && !automation.isFollowUp;

  return (
    <UiDialog title="Are you sure?" size="xs" onClose={onClose}>
      <UiDialog.Content>
        <div className="typo-title">{turnOffMessage}</div>
        {showDeleteFollowUps && (
          <div className="u-margin-top">
            <div className="typo-title">This automation has follow up messages</div>
            <div className="u-flex-center u-margin-bottom">
              <Checkbox onClick={handleToggleDeletingFollowUps} checked={isDeletingFollowUps} />
              <div className="typo-title u-flex-center">
                Delete all follow ups to this automation
              </div>
            </div>
          </div>
        )}
        <div className="typo-subtitle u-margin-top-sm">{orgDefaultMessage}</div>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton onClick={onClose} variant="dismiss">
          Cancel
        </UiButton>
        <div>
          {!!isActive && (
            <UiButton onClick={handleTurnOff} variant="secondary" className="u-margin-right">
              Turn Off
            </UiButton>
          )}
          <UiButton onClick={deleteAutomation} variant="primary">
            Delete
          </UiButton>
        </div>
      </UiDialog.Actions>
    </UiDialog>
  );
};
